/**
 * @file This file contains the TermsOfUse component which renders the terms of use sections for Essentia Analytics Limited's website.
 * Each section of the terms of use is defined in an array and then mapped to display with material-ui components for styling.
 */
import React from 'react';
import { PageLayout } from './PageLayout';
import { Typography, Container, Paper } from '@mui/material';
import { styled } from '@mui/system';

export const CenteredContainer = styled(Container)({
    display: 'flex',
    flexDirection: 'column',
    marginTop: '10rem',
    paddingLeft: '2rem',
    paddingRight: '2rem',
    paddingBottom: '6rem'
});

const sections = [
    {
        header: '1. Licence',
        contents: [
            {
                id: 'LIC1',
                text: 'Subject to these terms and conditions, we hereby provide you with a non-exclusive, revocable, limited licence to access the Site and view the EBAB for your own internal business or personal purposes.'
            }
        ]
    },
    {
        header: '1. Restrictions',
        contents: [
            {
                id: 'RES1',
                text:
                    'You agree not to: (a) systematically copy (whether by printing off onto paper, storing on disk or in any other way) substantial parts of the Site; (b) remove, change or obscure in any way anything on the Site or otherwise use any material contained on the Site except as set out in these terms and conditions; (c) reproduce, retransmit, disseminate, sell, publish, broadcast the Site or any portion of it, or use the Site or anything available on it in connection with creating, promoting, trading or marketing investment products without our express written consent or (d) use the Site for unlawful purposes or fail to comply with any applicable laws, statutes or regulations that apply to your access or use of the Site or any information taken or derived from it.' +
                    'Your further agree that you will not (a) not modify, adapt or create derivative works from the EBAB or copy the EBAB; (b) not reverse-engineer or decompile the EBAB; (c) use or analyse the EBAB in order to analyse the way in which the EBAB operates or the methodology behind the EBAB, whether as a means of developing a competing service (either by itself or using a third party to do so) or for any other purpose.'
            }
        ]
    },
    {
        header: '3. No Investment Advice',
        contents: [
            {
                id: 'NIA1',
                text:
                    'You acknowledge that:' +
                    "(a) we do not provide investment advice (as defined in the Financial Services and Markets Act 2000), that nothing on the Site constitutes investment advice and that you will not treat any of the Site's content as such;" +
                    '(b) we do not recommend any financial product nor recommend that any financial product should be bought, sold or held by you;' +
                    '(d) nothing on the Site should be construed as an offer, nor the solicitation of an offer, to buy or sell securities by us; and' +
                    '(e) information which may be referred to on the Site from time to time may not be suitable for you and that you should not make any investment decision without consulting a fully qualified financial adviser.'
            }
        ]
    },
    {
        header: '4. Access',
        contents: [
            {
                id: 'ACC1',
                text: 'The Site is directed at users located in the United Kingdom. Users from outside the UK are responsible for ensuring that they comply with all applicable local laws.'
            }
        ]
    },
    {
        header: '5. Privacy',
        contents: [
            {
                id: 'PRIV1',
                text: "Our use of your personal information is governed by Essentia's Privacy Policy, which forms part of these terms and conditions."
            }
        ]
    },
    {
        header: '6. Intellectual Property',
        contents: [
            {
                id: 'IPR1',
                text: 'All Intellectual Property Rights in and relating to the Site and the EBAB belong to us or our third party licensors.  You shall have no rights in or to the EBAB  and Documentation (including any Intellectual Property Rights therein) other than the right to use the Site in accordance with the terms of this Agreement.'
            },
            {
                id: 'IPR2',
                text: ' The Site contains certain trade marks. All trade marks included on the Site are the property of Essentia, or its content providers. You are not allowed to copy or otherwise use any of these trade marks in any way except as set out in these terms and conditions.'
            }
        ]
    },
    {
        header: '7. Limitations of liability',
        contents: [
            {
                id: 'LL1',
                text: '7.1 We do not exclude or limit its liability for death or personal injury resulting from our negligence, fraud or any other liability which may not by applicable law be excluded or limited.'
            },
            {
                id: 'LL2',
                text: '7.2 Subject to clause 7.1, in no event shall Essentia be liable (whether for breach of contract, negligence or for any other reason) for (i) any loss of profits, (ii) exemplary or special damages, (iii) loss of sales, (iv) loss of revenue, (v) loss of goodwill, (vi) loss of any software or data, (vii) loss of bargain, (viii) loss of opportunity, (ix) loss of use of computer equipment, software or data, (x) loss of or waste of management or other staff time, or (xi) for any indirect, consequential or special loss, however arising.'
            },
            {
                id: 'LL3',
                text: '7.3 Subject to clauses 7.1 and 7.2, the Essentia’s liability to you whether in contract, tort or otherwise is limited to £50 (fifty pounds). '
            }
        ]
    },
    {
        header: '8. Disclaimer',
        contents: [
            {
                id: 'DIS1',
                text: '8.1 All information contained on the site is for general informational use only and may not be relied upon by you for any purpose, including, without limitation, in making any investment decision. The site only provides information to you and does not provide investment advice and nothing on the site should be construed as being investment advice. Before making any investment choice you should always consult a fully qualified financial adviser.'
            },
            {
                id: 'DIS2',
                text: '8.2 Although we use reasonable efforts to ensure that information on the site including any information provided under the premium service is accurate and complete, we do not guarantee this to be the case. As a result, use of the site is at your sole risk and neither Essentia nor any other Essentia party can accept any liability for loss or damage suffered by you arising from your use of the site or any information contained on the site. You should take adequate steps to verify the accuracy and completeness of any information contained on the site.'
            },
            {
                id: 'DIS3',
                text: '8.3 Information contained on the site is not tailored for your individual situation and, as a result, such information may be unsuitable for you and your investment decisions. You should consult a financial adviser before making any investment decision.'
            },
            {
                id: 'DIS4',
                text: '8.4 We are not able to exercise control over the security or content of information passing over the internet, and we hereby exclude all liability of any kind for the transmission or reception of infringing or unlawful information of whatever nature.'
            },
            {
                id: 'DIS5',
                text: '8.5 We accept no liability for loss or damage suffered by you as a result of accessing site content which contains any virus or which has been maliciously corrupted.'
            }
        ]
    },
    {
        header: '9. Availability and updating of the Site',
        contents: [
            {
                id: 'AUS1',
                text: '9.1 We may suspend the operation of the site at any time without notice. We do not warrant that access to or use of the site or the EBAB will be uninterrupted or error free.'
            },
            {
                id: 'AUS2',
                text: '9.2 We may change the format and content of the site at its sole discretion at any time without notice. You should refresh your browser each time you visit the site to ensure that you access the most up-to-date version of the site and any information appearing on it.'
            }
        ]
    },
    {
        header: '10. Enquiries or complaints',
        contents: [
            {
                id: 'ENQ1',
                text: 'If you have any enquiries or complaints about the site then please address them (within 30 (thirty) days of such enquiry or complaint first arising) via the communication channels described here: support@essentia-analytics.com.'
            }
        ]
    },
    {
        header: '11. General and governing law',
        contents: [
            {
                id: 'GGL1',
                text: '11.1 These terms and conditions form the entire understanding of you and us with respect to the subject matter hereof and supersede all previous agreements, understandings, and representations relating to that subject matter. If any provision of these terms and conditions is found to be unenforceable, that finding will not affect the validity of any other provision. You agree that we may subcontract the performance of any of its obligations or may assign these terms and conditions or any of its rights or obligations without your consent or the need to give you notice.'
            },
            {
                id: 'GGL2',
                text: '11.2 Essentia will not be liable to you for any breach of these terms and conditions which arises because of any circumstances which Essentia cannot reasonably be expected to control.'
            },
            {
                id: 'GGL3',
                text: '11.3 These terms and conditions shall be governed and interpreted in accordance with English law, and you consent to the non-exclusive jurisdiction of the English courts.'
            }
        ]
    },
    {
        header: '12. Principal trading address and company details',
        contents: [
            {
                id: 'PTACD1',
                text: (
                    <>
                        Essentia Analytics Limited (company number 07164254)
                        <br />
                        Registered office: 1 Mark Square, London, EC2A 4EG.
                    </>
                )
            }
        ]
    }
];

/**
 * The TermsOfUse component renders the terms of use of the site.
 * It uses the PageLayout for consistent page structure, and maps through the sections array to
 * render each section using MUI's Paper and Typography components.
 *
 * @returns {React.Element} The TermsOfUse component which displays the terms of use on the website.
 */
const TermsOfUse = () => {
    return (
        <PageLayout>
            <CenteredContainer>
                <Typography
                    variant='header0'
                    align='center'
                    paddingBottom='1rem'
                >
                    ESSENTIA ANALYTICS LIMITED
                </Typography>
                <Typography
                    variant='header1'
                    align='center'
                    paddingBottom='1rem'
                >
                    Terms of Use for Site
                </Typography>
                <Typography variant='body2' paragraph>
                    These Terms and Conditions (&quot;Terms&quot;) set out the
                    terms on which Essentia Analytics Limited
                    (&quot;Essentia&quot; and/or &quot;we&quot;) licences use of
                    this site my.behavioral-alpha.com and the content contained
                    therein including the Essentia Behavioral Alpha Benchmark
                    (&quot;EBAB&quot;) (together the &quot;Site&quot;) to you.
                </Typography>
                {sections?.map((section) => (
                    <Paper elevation={0} key={section.header}>
                        <Typography
                            variant='header2'
                            style={{ fontWeight: '600' }}
                            paragraph
                        >
                            {section.header}
                        </Typography>
                        {section.contents?.map((contentItem) => (
                            <Typography
                                variant='body2'
                                paragraph
                                key={contentItem.id}
                            >
                                {contentItem.text}
                            </Typography>
                        ))}
                    </Paper>
                ))}
            </CenteredContainer>
        </PageLayout>
    );
};

export default TermsOfUse;
